import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import SearchableSelectBox from '../components/ui/SearchableSelectBox'; // Using SearchableSelectBox for catalogs
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import { Plus, Minus } from 'lucide-react'; // Icons for add/remove row
import { API_BASE_PATH, fetchCatalogs } from '../apiHelper'; // Import fetchCatalogs

// Initialize the API client
const client = new FastAPIClient();

/**
 * CreateCategories Component.
 * Allows users to create multiple category entries simultaneously for various catalogs.
 * Fetches catalogs dynamically and populates member_id from the logged-in user.
 * Designed with Tailwind CSS, adhering to the specified design principles.
 */
const CreateCategories = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // State to hold user info (specifically users_id for member_id)
    const [loggedInUser, setLoggedInUser] = useState(null);
    // State to hold fetched catalogs for the dropdown
    const [catalogOptions, setCatalogOptions] = useState([]);
    const [loadingCatalogs, setLoadingCatalogs] = useState(true);

    // Initial state for a single category entry
    const initialCategory = {
        member_id: 0, // Placeholder, will be set from loggedInUser
        identifier: '', // Category name
        catalog_id: '', // Selected catalog ID
        field1: '', // Category description
    };

    // State to manage multiple category entries
    const [categories, setCategories] = useState([initialCategory]);
    const [errors, setErrors] = useState({}); // Stores errors, keyed by row index and field name
    const [submitStatus, setSubmitStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'

    // Effect to load user from local storage and fetch catalogs
    useEffect(() => {
        const loadUserDataAndCatalogs = async () => {
            const lcUser = localStorage.getItem("user");
            if (lcUser) {
                const parsedUser = JSON.parse(lcUser);
                setLoggedInUser(parsedUser);

                // Update member_id for existing and future category entries
                setCategories(prevCategories =>
                    prevCategories.map(category => ({
                        ...category,
                        member_id: parsedUser.users_id || 0 // Use users_id from localStorage
                    }))
                );

                // Fetch catalogs using the member_id
                try {
                    setLoadingCatalogs(true);
                    const fetchedCatalogs = await fetchCatalogs(parsedUser);
                    const options = fetchedCatalogs.map(cat => ({
                        label: `${cat.identifier} (${cat.description})`,
                        value: cat.catalog_id
                    }));
                    setCatalogOptions(options);
                } catch (err) {
                    console.error("Error loading catalogs:", err);
                    showToast(err.response?.data?.detail || "Failed to load catalogs. Please try again.", "error");
                } finally {
                    setLoadingCatalogs(false);
                }
            } else {
                showToast("Please log in to create categories.", "info");
                // Optionally redirect to login if no user is found
                navigate('/login');
            }
        };

        loadUserDataAndCatalogs();
    }, [showToast]); // Dependency on showToast

    // Handler for changes in individual category input fields
    const handleCategoryInputChange = (index, e) => {
        const { name, value } = e.target;
        const updatedCategories = categories.map((category, i) =>
            i === index ? { ...category, [name]: value } : category
        );
        setCategories(updatedCategories);

        // Clear specific field error as user types
        if (errors[index] && errors[index][name]) {
            setErrors(prevErrors => {
                const newRowErrors = { ...prevErrors[index], [name]: '' };
                if (Object.values(newRowErrors).every(err => !err)) {
                    const newErrors = { ...prevErrors };
                    delete newErrors[index];
                    return newErrors;
                }
                return { ...prevErrors, [index]: newRowErrors };
            });
        }
    };

    // Adds a new empty category row
    const handleAddCategoryRow = useCallback(() => {
        setCategories(prevCategories => [
            ...prevCategories,
            { ...initialCategory, member_id: loggedInUser?.users_id || 0 } // Pre-fill member_id for new row
        ]);
    }, [loggedInUser, initialCategory]);

    // Removes a category row by index
    const handleRemoveCategoryRow = useCallback((index) => {
        setCategories(prevCategories => prevCategories.filter((_, i) => i !== index));
        // Also remove errors for the deleted row
        setErrors(prevErrors => {
            const newErrors = { ...prevErrors };
            delete newErrors[index];
            return newErrors;
        });
    }, []);

    // Client-side validation for all category entries
    const validateForm = useCallback(() => {
        let newErrors = {};
        let isValid = true;

        if (categories.length === 0) {
            newErrors.general = "At least one category entry is required.";
            isValid = false;
        }

        categories.forEach((category, index) => {
            let rowErrors = {};
            if (!category.identifier.trim()) {
                rowErrors.identifier = 'Category Identifier is required.';
                isValid = false;
            }
            if (!category.catalog_id) {
                rowErrors.catalog_id = 'Catalog is required.';
                isValid = false;
            }
            if (!category.field1.trim()) { // field1 is the description
                rowErrors.field1 = 'Category Description is required.';
                isValid = false;
            }
            if (Object.keys(rowErrors).length > 0) {
                newErrors[index] = rowErrors;
            }
        });

        setErrors(newErrors);
        return isValid;
    }, [categories]);

    // Form submission handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitStatus('loading');
        setErrors({}); // Clear previous errors

        if (!validateForm()) {
            setSubmitStatus('error');
            showToast("Please correct the errors in the form.", "error");
            return;
        }

        // Prepare payload: ensure member_id is set for each category
        const payload = categories.map(category => ({
            member_id: loggedInUser?.users_id || 0, // Final check for member_id
            identifier: category.identifier,
            catalog_id: category.catalog_id,
            field1: category.field1, // Map to field1 as per schema
        }));

        // console.log('Submitting categories payload:', payload);

        try {
            // Assuming the backend endpoint for creating multiple categories is '/product/create-category'
            const response = await client.postData(`${API_BASE_PATH}/product/create-category`, payload);
            // console.log('Categories created successfully:', response);
            showToast("Categories created successfully!", "success");

            // Clear the form by resetting to a single empty category
            setCategories([{ ...initialCategory, member_id: loggedInUser?.users_id || 0 }]);
            setSubmitStatus('success');
        } catch (err) {
            console.error('Failed to create categories:', err);
            const errorMessage = err.response?.data?.detail || `Failed to create categories: ${err.message || err}`;
            showToast(errorMessage, "error");
            setSubmitStatus('error');
        }
    };

    return (
        <div className="container mx-auto px-4 py-6">
            <Card className="p-6">
                <h2 className="text-2xl font-bold text-center text-text-primary mb-6">
                    Create New Categories
                </h2>
                <p className="text-center text-text-secondary mb-8 text-sm">
                    Organize your products by creating categories and assigning them to catalogs.
                </p>

                <form onSubmit={handleSubmit} noValidate>
                    {loadingCatalogs ? (
                        <div className="text-center text-text-secondary py-8">
                            Loading catalogs...
                        </div>
                    ) : catalogOptions.length === 0 ? (
                        <div className="text-center text-gmail-red py-8">
                            No catalogs found. Please create a catalog first.
                            <Button
                                type="button"
                                variant="primary"
                                size="sm"
                                onClick={() => navigate('/catalogs/create')}
                                className="mt-4 mx-auto block"
                            >
                                Go to Create Catalogs
                            </Button>
                        </div>
                    ) : (
                        categories.map((category, index) => (
                            <div key={index} className="flex flex-col md:flex-row items-end gap-4 mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50 relative">
                                {/* Remove button for all but the first row (if only one row, disable) */}
                                {categories.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveCategoryRow(index)}
                                        className="absolute top-2 right-2 p-1 rounded-full text-gray-500 hover:bg-red-100 hover:text-gmail-red
                                                   focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gmail-red"
                                        aria-label={`Remove category entry ${index + 1}`}
                                    >
                                        <Minus size={20} />
                                    </button>
                                )}
                                <div className="flex-1 w-full">
                                    <Input
                                        label={`Category ${index + 1} Identifier`}
                                        id={`identifier-${index}`}
                                        name="identifier"
                                        type="text"
                                        placeholder="e.g., Electronics, Apparel"
                                        value={category.identifier}
                                        onChange={(e) => handleCategoryInputChange(index, e)}
                                        required
                                        error={errors[index]?.identifier}
                                    />
                                    {errors[index]?.identifier && <p className="text-gmail-red text-xs mt-[-10px] mb-4">{errors[index].identifier}</p>}
                                </div>
                                <div className="flex-1 w-full">
                                    <SearchableSelectBox
                                        label={`Category ${index + 1} Catalog`}
                                        id={`catalog_id-${index}`}
                                        name="catalog_id"
                                        value={category.catalog_id}
                                        onChange={(e) => handleCategoryInputChange(index, e)}
                                        options={catalogOptions}
                                        placeholder="Select a catalog"
                                        required
                                        error={errors[index]?.catalog_id}
                                    />
                                    {errors[index]?.catalog_id && <p className="text-gmail-red text-xs mt-[-10px] mb-4">{errors[index].catalog_id}</p>}
                                </div>
                                <div className="flex-1 w-full">
                                    <Input
                                        label={`Category ${index + 1} Description`}
                                        id={`field1-${index}`} // Mapped to 'field1' in payload
                                        name="field1"
                                        type="text"
                                        placeholder="Brief description of this category"
                                        value={category.field1}
                                        onChange={(e) => handleCategoryInputChange(index, e)}
                                        required
                                        error={errors[index]?.field1}
                                    />
                                    {errors[index]?.field1 && <p className="text-gmail-red text-xs mt-[-10px] mb-4">{errors[index].field1}</p>}
                                </div>
                            </div>
                        ))
                    )}

                    {errors.general && <p className="text-gmail-red text-sm text-center mb-4">{errors.general}</p>}

                    {/* Only show Add/Submit buttons if catalogs are loaded and available */}
                    {!loadingCatalogs && catalogOptions.length > 0 && (
                        <div className="flex justify-between items-center mt-6">
                            <Button
                                type="button"
                                onClick={handleAddCategoryRow}
                                variant="secondary"
                                className="flex items-center gap-2"
                            >
                                <Plus size={20} />
                                Add Another Category
                            </Button>

                            <Button
                                type="submit"
                                variant="primary"
                                size="lg"
                                disabled={submitStatus === 'loading'}
                                className="flex items-center gap-2"
                            >
                                {submitStatus === 'loading' ? 'Creating Categories...' : 'Create Categories'}
                            </Button>
                        </div>
                    )}
                </form>
            </Card>
        </div>
    );
};

export default CreateCategories;
